import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const EMICalculator = () => {
  // State for basic inputs
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [loanTenure, setLoanTenure] = useState(12);
  const [interestRate, setInterestRate] = useState(8);
  
  // State for advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [repaymentYears, setRepaymentYears] = useState(1);
  const [loanType, setLoanType] = useState('home');
  
  // State for share toast
  const [showShareToast, setShowShareToast] = useState(false);
  
  // State for tooltips
  const [showToolTip, setShowToolTip] = useState('');
  
  // State for calculation results
  const [result, setResult] = useState({
    emi: 0,
    totalAmount: 0,
    outstandingAmount: 0,
    principleRepaid: 0,
    interestPaid: 0,
    totalInterest: 0
  });

  // Calculate EMI details
  const calculateEMI = () => {
    // Monthly interest rate
    const monthlyInterestRate = (interestRate / 12) / 100;
    
    // Total number of months
    const totalMonths = loanTenure * 12;
    const repaidMonths = repaymentYears * 12;
    
    // Calculate EMI
    const emi = loanAmount * monthlyInterestRate * 
                Math.pow(1 + monthlyInterestRate, totalMonths) / 
                (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);
    
    // Calculate total amount
    const totalAmount = emi * totalMonths;
    
    // Calculate total interest
    const totalInterest = totalAmount - loanAmount;
    
    // Calculate principle and interest paid
    const monthlyPrincipal = loanAmount / totalMonths;
    const principleRepaid = monthlyPrincipal * repaidMonths;
    const interestPaid = (emi - monthlyPrincipal) * repaidMonths;
    const outstandingAmount = totalAmount - (emi * repaidMonths);
    
    return {
      emi: Math.round(emi),
      totalAmount: Math.round(totalAmount),
      outstandingAmount: Math.round(outstandingAmount),
      principleRepaid: Math.round(principleRepaid),
      interestPaid: Math.round(interestPaid),
      totalInterest: Math.round(totalInterest)
    };
  };

  // Recalculate whenever inputs change
  useEffect(() => {
    setResult(calculateEMI());
  }, [
    loanAmount,
    loanTenure,
    interestRate,
    repaymentYears,
    loanType
  ]);

  // Handle share button click
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">EMI Calculator</h1>
        <h2 className="text-lg text-gray-600">Estimate your monthly loan payments and total interest cost</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Loan Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Loan Details</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Loan Amount
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('loanAmount')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'loanAmount' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Total amount borrowed excluding any down payment
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={50000}
                    max={10000000}
                    step={10000}
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹50K</span>
                    <span>₹1Cr</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Terms */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Loan Terms</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Interest Rate
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('interestRate')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'interestRate' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Annual interest rate applied to your loan
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={4}
                    max={20}
                    step={0.1}
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>4%</span>
                    <span>20%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Loan Tenure</label>
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>30 years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Loan Type</h2>
              <div className="grid grid-cols-3 gap-4">
                <div 
                  className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                    loanType === 'home' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setLoanType('home')}
                >
                  <p className="font-medium">Home Loan</p>
                  <p className="text-xs text-gray-500 mt-1">For real estate</p>
                </div>
                <div 
                  className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                    loanType === 'car' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setLoanType('car')}
                >
                  <p className="font-medium">Car Loan</p>
                  <p className="text-xs text-gray-500 mt-1">For vehicles</p>
                </div>
                <div 
                  className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                    loanType === 'personal' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setLoanType('personal')}
                >
                  <p className="font-medium">Personal Loan</p>
                  <p className="text-xs text-gray-500 mt-1">For other needs</p>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Already Repaid
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('repayment')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'repayment' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              How many years of loan repayment have you already made
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={repaymentYears}
                        onChange={(e) => setRepaymentYears(Math.min(loanTenure, Number(e.target.value)))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={loanTenure}
                        step={1}
                        value={repaymentYears}
                        onChange={(e) => setRepaymentYears(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0 years</span>
                        <span>{loanTenure} years</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[520px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">EMI Details</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.emi)}
                  </div>
                  <div className="text-sm text-gray-300">Monthly EMI</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Principal Amount</span>
                    <span className="font-bold">{formatCurrency(loanAmount)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Interest</span>
                    <span className="font-bold">{formatCurrency(result.totalInterest)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Amount</span>
                    <span className="font-bold">{formatCurrency(result.totalAmount)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Principle Repaid</span>
                    <span className="font-bold">{formatCurrency(result.principleRepaid)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Interest Paid</span>
                    <span className="font-bold">{formatCurrency(result.interestPaid)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Outstanding Amount</span>
                    <span className="font-bold">{formatCurrency(result.outstandingAmount)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Apply for Loan →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;